import React, {useCallback, useEffect, useState} from 'react';
import DialogComponent from 'components/Dialog';
import {Trans, useTranslation} from 'react-i18next';
import {DialogTypes} from '../../../../../common/names';
import i18n from '../../../../../i18n';
import Button from '../../../../../components/Button';
import {useDispatch, useSelector} from 'react-redux';
import ConfirmationWrapper from '../../../../../components/buttons/ConfirmationWrapper';
import {IProxyServer} from '../../../../../types/ProxyServer';
import {pageActions, pageSelectors} from '../../../CommonPage/redux';
import AddEditProxyDialog from './AddEditProxyDialog';
import ViewProxyDialog from './ViewProxyDialog';
import {PrimaryButton} from 'components/buttons/PrimaryButton';
import {WarningButton} from 'components/buttons/WarningButton';
import {GridColDef, DataGridPro} from '@mui/x-data-grid-pro';
import {Grid} from '@mui/material';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {ReactFChildrenProps} from 'types/react/ReactFChildrenProps';

enum ProxyOperation {
   NoOperation,
   Add,
   Edit,
   View,
}

const cols: GridColDef[] = [
   {
      field: 'defaultProxy',
      headerName: ' ',
      flex: 50,
      valueFormatter: (params) => (params.value ? '*' : ''),
      align: 'center',
   },
   {field: 'name', headerName: i18n.t('Name'), flex: 200},
   {field: 'description', headerName: i18n.t('Description'), flex: 300},
];

type ProxiesDialogProps = {
   onClose: () => void;
};
const ProxiesDialog: ReactFChildrenProps<ProxiesDialogProps> = (props) => {
   const {t} = useTranslation();
   const {onClose} = props;
   const [selected, setSelected] = useState<IProxyServer>();
   const [operation, setOperation] = useState<ProxyOperation>(ProxyOperation.NoOperation);
   const clearOperation = React.useCallback(() => {
      setOperation(ProxyOperation.NoOperation);
   }, [setOperation]);

   const dispatch = useDispatch();
   const proxies = useSelector(pageSelectors.proxies);
   const fetchProxies = useCallback(() => dispatch(pageActions.getProxies()), [dispatch]);
   const onSuccess = useCallback(
      (p: IProxyServer) => {
         fetchProxies();
         if (p.id === selected?.id) {
            setSelected(p);
         }
         clearOperation();
      },
      [fetchProxies, selected, clearOperation]
   );
   useEffect(() => {
      fetchProxies();
   }, [fetchProxies]);

   const add = useCallback(
      (proxy: IProxyServer, formikBag: FormOnSubmitHelpers<IProxyServer>) =>
         dispatch(
            pageActions.addProxy(proxy, {
               onSuccess: onSuccess,
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         ),
      [dispatch, onSuccess]
   );
   const edit = useCallback(
      (proxy: IProxyServer, formikBag: FormOnSubmitHelpers<IProxyServer>) =>
         dispatch(
            pageActions.editProxy(proxy, {
               onSuccess: onSuccess,
               onError: (errors: any) => formikBag.setErrors(errors),
            })
         ),
      [dispatch, onSuccess]
   );
   const deleteProxy = useCallback(
      () =>
         selected &&
         dispatch(
            pageActions.deleteProxy(selected.id, {
               onSuccess: fetchProxies,
            })
         ),
      [dispatch, fetchProxies, selected]
   );
   const onSubmit = (proxy: IProxyServer, formikBag: FormOnSubmitHelpers<IProxyServer>) => {
      if (operation === ProxyOperation.Add) {
         add(proxy, formikBag);
         return;
      }
      edit(proxy, formikBag);
   };
   return (
      <DialogComponent
         maxWidth="md"
         header={DialogTypes.SettingsProxiesDialog.dialogName}
         close={onClose}
      >
         <div style={{height: 400, width: '100%', marginBottom: '16px'}}>
            <DataGridPro
               rows={proxies}
               columns={cols}
               pageSize={5}
               disableMultipleSelection
               onSelectionModelChange={(selectionModel) =>
                  setSelected(proxies.find((p) => p.id === selectionModel[0]) as IProxyServer)
               }
            />
         </div>
         <Grid container spacing={3} item md={8}>
            <Grid item>
               <PrimaryButton onClick={() => setOperation(ProxyOperation.Add)}>
                  <Trans>Add</Trans>
               </PrimaryButton>
            </Grid>
            <Grid item>
               <ConfirmationWrapper
                  content={t('Do you want to delete the Proxy Server named {{name}}', {
                     name: selected?.name,
                  })}
                  onOk={deleteProxy}
               >
                  <Button color="secondary" disabled={!selected}>
                     <Trans>Delete</Trans>
                  </Button>
               </ConfirmationWrapper>
            </Grid>
            <Grid item>
               <PrimaryButton
                  onClick={() => setOperation(ProxyOperation.Edit)}
                  disabled={!selected}
               >
                  <Trans>Edit</Trans>
               </PrimaryButton>
            </Grid>
            <Grid item>
               <WarningButton
                  onClick={() => setOperation(ProxyOperation.View)}
                  disabled={!selected}
               >
                  <Trans>View</Trans>
               </WarningButton>
            </Grid>
         </Grid>
         <Grid container spacing={3} justifyContent="center">
            <Grid item>
               <Button type="button" onClick={onClose}>
                  <Trans>Close</Trans>
               </Button>
            </Grid>
         </Grid>

         {operation === ProxyOperation.Add || operation === ProxyOperation.Edit ? (
            <AddEditProxyDialog
               proxy={operation === ProxyOperation.Edit ? selected : undefined}
               onSubmit={onSubmit}
               onClose={clearOperation}
            />
         ) : null}
         {selected && operation === ProxyOperation.View ? (
            <ViewProxyDialog proxy={selected} onClose={clearOperation} />
         ) : null}
      </DialogComponent>
   );
};

export default React.memo(ProxiesDialog);
